import { useState, useCallback, useRef } from 'react'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog'

type ConfirmOpts = {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  destructive?: boolean
}

type State = ConfirmOpts & { open: boolean; resolve: (v: boolean) => void }

let _opener: ((opts: ConfirmOpts) => Promise<boolean>) | null = null

/**
 * `await confirm({ title, description })` — replaces native window.confirm
 * with a styled shadcn AlertDialog. The provider must be mounted once at
 * the app root via <ConfirmDialogHost />.
 */
export function confirm(opts: ConfirmOpts): Promise<boolean> {
  if (!_opener) {
    // Provider not mounted (shouldn't happen). Fall back to native.
    if (typeof window !== 'undefined') return Promise.resolve(window.confirm(opts.title))
    return Promise.resolve(false)
  }
  return _opener(opts)
}

export function ConfirmDialogHost() {
  const [state, setState] = useState<State | null>(null)
  // Latest resolver — used by Cancel/Confirm without re-binding callbacks.
  const stateRef = useRef<State | null>(null)
  stateRef.current = state

  const open = useCallback((opts: ConfirmOpts) => {
    return new Promise<boolean>((resolve) => {
      setState({ open: true, resolve, ...opts })
    })
  }, [])

  // Register the opener exactly once.
  if (_opener !== open) _opener = open

  const close = (result: boolean) => {
    const cur = stateRef.current
    if (cur) cur.resolve(result)
    setState(null)
  }

  if (!state) return null

  return (
    <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false) }}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription className="text-sm">
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)}>
            {state.cancelText || 'Отмена'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close(true)}
            className={state.destructive
              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              : ''}
          >
            {state.confirmText || 'OK'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
